/**
 * useWledEffects Composable
 * 
 * Manages WLED effects state for lights in a scene.
 * Handles effect selection, gradient editing, and syncing to devices.
 * 
 * Effect settings are stored per-scene in scene_light_configs.wled_effect_settings
 */

import { ref, computed, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { wledDeviceService } from '@/services/wled/WledDeviceService'
import { useWledDevicesStore } from '@/stores/wledDevices'
import { useLightsStore, type Light } from '@/stores/lights'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { 
  type WledEffect, 
  type WledPalette, 
  type WledEffectState,
  type Gradient,
  type GradientStop,
  DEFAULT_GRADIENT,
  COMMON_EFFECTS,
  COMMON_PALETTES,
  generateStopId,
  hueHsbToRgb
} from '@/types/wledEffect'

/**
 * Create a composable instance for managing WLED effects
 * @param lightId - The light ID
 * @param sceneId - The scene ID (effects are stored per-scene)
 */
export function useWledEffects(lightId: string, sceneId: string) {
  const lightsStore = useLightsStore()
  const wledDevicesStore = useWledDevicesStore()
  const sceneLightConfigsStore = useSceneLightConfigsStore()
  
  // Effect state
  const effectId = ref(0) // 0 = Solid effect (default)
  const effectName = ref('Solid')
  const speed = ref(128) // 0-255, default middle
  const intensity = ref(128) // 0-255, default middle
  const paletteId = ref<number | undefined>(undefined)
  
  // Gradient state - deep copy default to avoid shared references
  const gradient = ref<Gradient>({ 
    stops: DEFAULT_GRADIENT.stops.map(s => ({ 
      ...s, 
      id: generateStopId(),
      color: { ...s.color }
    }))
  })
  
  // Available effects/palettes (fetched from device)
  const availableEffects = ref<WledEffect[]>([...COMMON_EFFECTS])
  const availablePalettes = ref<WledPalette[]>([...COMMON_PALETTES])
  
  // Loading states
  const isLoading = ref(false)
  const isSyncing = ref(false)
  const isInitializing = ref(false) // Prevent watchers from saving during initialization
  
  // Get the light
  const light = computed(() => lightsStore.getLight(lightId))
  
  // Get the direct scene config for this light in this scene (for checking if we have local settings)
  const directConfig = computed(() => sceneLightConfigsStore.getConfig(sceneId, lightId))
  
  // Get the resolved scene config with inheritance (what we actually use)
  const resolvedConfig = computed(() => sceneLightConfigsStore.getResolvedConfig(sceneId, lightId))
  
  // Check if the current config is inherited
  const isInherited = computed(() => resolvedConfig.value?.is_inherited ?? false)
  
  // Get the name of the scene we're inheriting from
  const inheritedFromSceneName = computed(() => resolvedConfig.value?.inherited_from_scene_name ?? null)
  
  // Get the WLED device for this light
  const wledDevice = computed(() => {
    if (!light.value?.wled_device_id) return null
    return wledDevicesStore.getDevice(light.value.wled_device_id)
  })
  
  // Check if this is a WLED light
  const isWledLight = computed(() => !!light.value?.wled_device_id)
  
  // Current effect state as an object
  const effectState = computed((): WledEffectState => ({
    effectId: effectId.value,
    effectName: effectName.value,
    speed: speed.value,
    intensity: intensity.value,
    paletteId: paletteId.value,
    gradient: gradient.value
  }))
  
  /**
   * Fetch available effects from the WLED device
   */
  async function fetchEffects() {
    if (!wledDevice.value) return
    
    isLoading.value = true
    try {
      const effects = await wledDeviceService.getEffects(wledDevice.value.ip_address)
      if (effects) {
        availableEffects.value = effects.map((name, id) => ({ id, name }))
      }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Fetch available palettes from the WLED device
   */
  async function fetchPalettes() {
    if (!wledDevice.value) return
    
    isLoading.value = true
    try {
      const palettes = await wledDeviceService.getPalettes(wledDevice.value.ip_address)
      if (palettes) {
        availablePalettes.value = palettes.map((name, id) => ({ id, name }))
      }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Set the current effect
   */
  function setEffect(id: number, name?: string) {
    effectId.value = id
    effectName.value = name || availableEffects.value.find(e => e.id === id)?.name || `Effect ${id}`
  }
  
  /**
   * Set effect speed
   */
  function setSpeed(value: number) {
    speed.value = Math.max(0, Math.min(255, value))
  }
  
  /**
   * Set effect intensity
   */
  function setIntensity(value: number) {
    intensity.value = Math.max(0, Math.min(255, value))
  }
  
  /**
   * Set palette
   */
  function setPalette(id: number | undefined) {
    paletteId.value = id
  }
  
  /**
   * Add a gradient stop
   */
  function addGradientStop(position: number, color: GradientStop['color']) {
    const newStop: GradientStop = {
      id: generateStopId(),
      position: Math.max(0, Math.min(1, position)),
      color
    }
    
    gradient.value.stops.push(newStop)
    // Sort by position
    gradient.value.stops.sort((a, b) => a.position - b.position)
    
    return newStop
  }
  
  /**
   * Update a gradient stop's position
   */
  function updateStopPosition(stopId: string, position: number) {
    const stop = gradient.value.stops.find(s => s.id === stopId)
    if (stop) {
      stop.position = Math.max(0, Math.min(1, position))
      // Re-sort by position
      gradient.value.stops.sort((a, b) => a.position - b.position)
    }
  }
  
  /**
   * Update a gradient stop's color
   */
  function updateStopColor(stopId: string, color: GradientStop['color']) {
    const stop = gradient.value.stops.find(s => s.id === stopId)
    if (stop) {
      stop.color = { ...color }
    }
  }
  
  /**
   * Remove a gradient stop (minimum 2 stops required)
   */
  function removeGradientStop(stopId: string) {
    if (gradient.value.stops.length <= 2) return false
    
    // Filter out the stop (creates new array for proper reactivity)
    const newStops = gradient.value.stops.filter(s => s.id !== stopId)
    if (newStops.length !== gradient.value.stops.length) {
      gradient.value = { stops: newStops }
      return true
    }
    return false
  }
  
  /**
   * Reset gradient to default
   */
  function resetGradient() {
    gradient.value = { 
      stops: DEFAULT_GRADIENT.stops.map(s => ({ 
        ...s, 
        id: generateStopId(),
        color: { ...s.color }
      }))
    }
  }
  
  /**
   * Set to solid color mode - keeps only the first stop color
   * Creates two stops with the same color (required minimum)
   */
  function setSolidColor() {
    const firstColor = gradient.value.stops[0]?.color || { hue: 0, saturation: 254, brightness: 254 }
    gradient.value = {
      stops: [
        { id: generateStopId(), position: 0, color: { ...firstColor } },
        { id: generateStopId(), position: 1, color: { ...firstColor } }
      ]
    }
  }
  
  /**
   * Clear all effect settings and return to inherited state
   * After clearing, reloads any inherited settings from parent scene
   */
  async function clearEffectSettings() {
    if (!light.value) return
    
    // Prevent watchers from saving while we reset
    isInitializing.value = true
    
    // Clear from database (set to null) - this returns to inherited state
    await sceneLightConfigsStore.updateWledEffectSettings(sceneId, lightId, null)
    
    // Wait for store to update
    await new Promise(resolve => setTimeout(resolve, 50))
    
    // Now reload from resolved config (which will include any inherited settings)
    loadSavedSettings()
    
    // If there are no inherited settings either, reset to defaults
    if (!resolvedConfig.value?.wled_effect_settings) {
      effectId.value = 0
      effectName.value = 'Solid'
      speed.value = 128
      intensity.value = 128
      paletteId.value = undefined
      resetGradient()
    }
    
    // Apply the current settings (inherited or defaults) to device
    if (wledDevice.value) {
      const segmentId = light.value.wled_segment_id ?? 0
      await wledDeviceService.setSegmentEffect(
        wledDevice.value.ip_address,
        segmentId,
        {
          effectId: effectId.value,
          speed: speed.value,
          intensity: intensity.value,
          paletteId: paletteId.value,
          colors: gradientToWledColors(),
          transitionTime: 400
        }
      )
    }
    
    // Re-enable watchers
    isInitializing.value = false
  }
  
  /**
   * Convert gradient stops to WLED colors array
   * WLED supports up to 3 colors for effects
   */
  function gradientToWledColors(): number[][] {
    // Take first 3 stops (or pad with last color)
    const colors: number[][] = []
    const stops = gradient.value.stops
    
    for (let i = 0; i < 3; i++) {
      const stop = stops[i] || stops[stops.length - 1]
      colors.push(hueHsbToRgb(stop.color.hue, stop.color.saturation, stop.color.brightness))
    }
    
    return colors
  }
  
  /**
   * Apply current effect state to the WLED device
   */
  async function applyEffect() {
    if (!wledDevice.value || !light.value) return false
    
    isSyncing.value = true
    try {
      const segmentId = light.value.wled_segment_id ?? 0
      
      const result = await wledDeviceService.setSegmentEffect(
        wledDevice.value.ip_address,
        segmentId,
        {
          effectId: effectId.value,
          speed: speed.value,
          intensity: intensity.value,
          paletteId: paletteId.value,
          colors: gradientToWledColors(),
          transitionTime: 400
        }
      )
      
      return result
    } finally {
      isSyncing.value = false
    }
  }
  
  /**
   * Load saved effect settings from the scene config (with inheritance support)
   */
  function loadSavedSettings() {
    // Load from resolved config (includes inheritance)
    const saved = resolvedConfig.value?.wled_effect_settings
    if (!saved) return
    
    effectId.value = saved.effectId ?? 0
    effectName.value = saved.effectName ?? 'Solid'
    speed.value = saved.speed ?? 128
    intensity.value = saved.intensity ?? 128
    paletteId.value = saved.paletteId
    
    if (saved.gradient?.stops && saved.gradient.stops.length >= 2) {
      gradient.value = {
        stops: saved.gradient.stops.map(s => ({
          ...s,
          color: { ...s.color }
        }))
      }
    }
  }
  
  /**
   * Initialize - load saved settings, fetch effects and palettes from device
   * If no settings exist (direct or inherited), save the defaults immediately
   */
  async function initialize() {
    if (!isWledLight.value) return
    
    // Set flag to prevent watchers from triggering saves during load
    isInitializing.value = true
    
    try {
      // Load saved settings first (from resolved config with inheritance)
      const hasSavedSettings = !!resolvedConfig.value?.wled_effect_settings?.gradient
      loadSavedSettings()
      
      // Wait a tick for watchers to fire (and be ignored due to flag)
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // If no saved settings (direct OR inherited), save defaults immediately so gradient pips show on wheel
      // Only save if we don't have any settings at all (not even inherited ones)
      if (!hasSavedSettings && !directConfig.value?.wled_effect_settings) {
        await sceneLightConfigsStore.updateWledEffectSettings(sceneId, lightId, {
          effectId: effectId.value,
          effectName: effectName.value,
          speed: speed.value,
          intensity: intensity.value,
          paletteId: paletteId.value,
          gradient: gradient.value
        })
      }
      
      // Then fetch available effects/palettes from device
      await Promise.all([
        fetchEffects(),
        fetchPalettes()
      ])
    } finally {
      isInitializing.value = false
    }
  }
  
  // Debounced sync to WLED device - push changes after 300ms of inactivity
  const debouncedSyncToDevice = useDebounceFn(async () => {
    if (!wledDevice.value || !light.value) return
    
    await applyEffect()
  }, 300)
  
  // Debounced save to DB - save after 500ms of inactivity  
  const debouncedSaveToDb = useDebounceFn(async () => {
    if (!light.value) return
    
    await sceneLightConfigsStore.updateWledEffectSettings(sceneId, lightId, {
      effectId: effectId.value,
      effectName: effectName.value,
      speed: speed.value,
      intensity: intensity.value,
      paletteId: paletteId.value,
      gradient: gradient.value
    })
  }, 500)
  
  // Watch for changes and auto-sync
  watch(
    [effectId, speed, intensity, paletteId],
    () => {
      // Skip if initializing (loading inherited values) to prevent overwriting inheritance
      if (isInitializing.value) return
      if (wledDevice.value && light.value) {
        debouncedSyncToDevice()
        debouncedSaveToDb()
      }
    }
  )
  
  // Deep watch gradient for changes
  watch(
    () => gradient.value.stops.map(s => `${s.position}:${s.color.hue}:${s.color.saturation}:${s.color.brightness}`).join(','),
    () => {
      // Skip if initializing (loading inherited values) to prevent overwriting inheritance
      if (isInitializing.value) return
      if (wledDevice.value && light.value) {
        debouncedSyncToDevice()
        debouncedSaveToDb()
      }
    }
  )

  return {
    // State
    effectId,
    effectName,
    speed,
    intensity,
    paletteId,
    gradient,
    
    // Computed
    light,
    wledDevice,
    isWledLight,
    effectState,
    availableEffects,
    availablePalettes,
    isInherited,
    inheritedFromSceneName,
    
    // Loading states
    isLoading,
    isSyncing,
    
    // Actions
    setEffect,
    setSpeed,
    setIntensity,
    setPalette,
    addGradientStop,
    updateStopPosition,
    updateStopColor,
    removeGradientStop,
    resetGradient,
    setSolidColor,
    clearEffectSettings,
    gradientToWledColors,
    applyEffect,
    fetchEffects,
    fetchPalettes,
    initialize
  }
}
